<!-- This component mimicks a <select>
     See https://developer.mozilla.org/en-US/docs/Learn/Forms/How_to_build_custom_form_controls -->
<script module lang="ts">
  import type { ImgSrcInput } from '#app/lib/image_source'

  export interface SelectOption {
    value: string
    text: string
    icon?: string
    image?: ImgSrcInput
    count?: number
    promise?: Promise<void>
  }
</script>
<script lang="ts">
  import { uniqueId } from 'lodash-es'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import Dropdown from '#components/dropdown.svelte'
  import SelectDropdownOption from '#components/select_dropdown_option.svelte'
  import Spinner from '#general/components/spinner.svelte'
  import { I18n } from '#user/lib/i18n'
  import type { Snippet } from 'svelte'

  let {
    value = $bindable(),
    resetValue,
    options,
    buttonLabel,
    withImage = false,
    hideCurrentlySelectedOption = false,
    loadingMessage,
    waitingForOptions,
    selectedOptionLineEnd,
    onSelectSameOption,
  }: {
    value: unknown
    resetValue?: unknown
    options: SelectOption[] | readonly SelectOption[]
    buttonLabel?: string
    withImage?: boolean
    hideCurrentlySelectedOption?: boolean
    loadingMessage?: string
    waitingForOptions?: Promise<void>
    selectedOptionLineEnd?: Snippet
    onSelectSameOption?: () => void
  } = $props()

  const buttonId = uniqueId('button')

  function onKeyDown (e) {
    const key = getActionKey(e)
    if (key === 'up') selectNext(-1)
    else if (key === 'down') selectNext(1)
    else return
    e.stopPropagation()
    e.preventDefault()
  }

  const optionPlaceholder = { value: 'all', text: I18n('all') }
  const currentOption = $derived(options?.find(option => option.value === value) || optionPlaceholder)

  function selectNext (indexIncrement: number) {
    const currentOptionIndex = options.indexOf(currentOption)
    const nextOption = options[currentOptionIndex + indexIncrement]
    if (nextOption) value = nextOption.value
  }

  function assignNewValue (option: SelectOption) {
    if (value === option.value) {
      onSelectSameOption()
    } else {
      value = option.value
    }
  }

  function resetFilter (e: ClickEvent) {
    value = resetValue
    e.stopPropagation()
  }
</script>

<div
  class="select-dropdown"
  class:has-image={withImage}
  role="listbox"
  onkeydown={onKeyDown}
  tabindex="-1"
>
  {#if buttonLabel}
    <!-- Set the title in case the text overflow is hidden -->
    <label for={buttonId} title={buttonLabel}>{buttonLabel}</label>
  {/if}
  <Dropdown
    alignButtonWidthOnDropdown={true}
    clickOnContentShouldCloseDropdown={true}
    {buttonId}
    buttonRole="listbox"
  >
    {#snippet buttonInner()}
      <div class="button-inner">
        <SelectDropdownOption option={currentOption} {withImage} />
        {#if resetValue && value !== resetValue}
          <button
            class="reset"
            title={I18n('reset filter')}
            onclick={resetFilter}
          >{@html icon('close')}</button>
        {/if}
      </div>
    {/snippet}
    {#snippet dropdownContent()}
      <div role="presentation" class="dropdown-content">
        {#await waitingForOptions}
          <div class="loading-message">
            <Spinner />
            {#if loadingMessage}<p>{loadingMessage}</p>{/if}
          </div>
        {:then}
          {#each options as option (option.value)}
            {#if !(hideCurrentlySelectedOption && option.value === value)}
              <button
                role="option"
                title={option.text}
                aria-selected={option.value === value}
                onclick={() => assignNewValue(option)}
              >
                <SelectDropdownOption {option} {withImage}>
                  {#snippet lineEnd()}
                    <div>
                      {#if option.value === value}
                        {@render selectedOptionLineEnd?.()}
                      {/if}
                    </div>
                  {/snippet}
                </SelectDropdownOption>
              </button>
            {/if}
          {/each}
        {/await}
      </div>
    {/snippet}
  </Dropdown>
</div>

<style lang="scss">
  @import "general/scss/select_dropdown_commons";
  label{
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .loading-message{
    margin: 1em 0;
    padding: 0 1em;
    text-align: center;
    white-space: break-spaces;
  }
</style>
